import React, { useState } from 'react'

import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'

import Container from 'components/layouts/atoms/Container'
import Typography from 'components/common/atoms/Typography'
import SectionContainer from 'components/layouts/atoms/SectionContainer'
import SVGIcon from 'components/common/atoms/SVGIcon'
import Tooltip from 'components/common/molecules/Tooltip'
import Modal from 'components/common/molecules/Modal'

import MarkdownContent from 'components/common/atoms/MarkdownContent'
import Button from 'components/common/atoms/Button'
import koinworksTableData from './KoinworksTableData.json'

type Requirement = {
  document: string
  notes?: string
  studMarried: boolean
  studNotMarried: boolean
}

const koinworksTableStyle = (theme: ThemeType) => css`
  .koinworks-table {
    &__table-wrapper {
      margin-top: 32px;
      display: block;
      overflow-x: auto;
      white-space: nowrap;
      padding: 12px;
    }
    &__table {
      border-radius: 10px;
      box-shadow: 0 2px 12px 0 rgba(26, 52, 79, 0.1);
      border-collapse: collapse;
      border-style: hidden;
      width: 100%;
      th {
        padding: 24px;
        border: 1px solid ${theme.gray.gray30};
        text-align: left;
      }
      td {
        padding: 12px 24px;
        border-right: 1px solid ${theme.gray.gray30};
      }
      tbody > tr {
        &:first-of-type {
          td {
            padding-top: 24px;
          }
        }
        &:last-child {
          td {
            padding-bottom: 24px;
          }
        }
      }
    }
    &__married {
      min-width: 225px;
      .icon-wrapper {
        margin: auto;
      }
    }
    &__document {
      min-width: 275px;
      span,
      a {
        margin-left: 12px;
        display: inline-block;
        cursor: pointer;
        &:hover {
          color: ${theme.gray.gray30};
        }
      }
    }
    &__flex {
      display: flex;
      align-items: center;
    }
    &__no {
      text-align: center;
    }
    &__note-sp {
      display: block;
    }
    &__note-pc {
      display: none !important;
    }
    @media screen and (min-width: 768px) {
      &__title {
        text-align: center;
      }
      &__table-wrapper {
        overflow-x: hidden;
        margin-top: 40px;
      }
      &__document {
        min-width: 645px;
      }
      &__no {
        min-width: 72px;
      }
      &__married {
        min-width: 224px;
      }
      &__note-sp {
        display: none !important;
      }
      &__note-pc {
        display: inline-block !important;
      }
    }
    .tooltip {
      width: 200px;
    }
  }
`

const KoinworksTable = () => {
  const theme: ThemeType = useTheme()
  const [modalIsShow, setModalShow] = useState(false)
  const [notes, setNotes] = useState('')
  const showNotes = (content: string) => {
    setModalShow(true)
    setNotes(content)
  }
  return (
    <div className="koinworks-table" css={koinworksTableStyle(theme)}>
      <SectionContainer withPaddingBottom>
        <Container>
          <Typography variant="title" level="2" tagName="h2" fontWeight="bold" className="koinworks-table__title">
            Yang Perlu Disiapkan
          </Typography>
          <div className="koinworks-table__table-wrapper">
            <table className="koinworks-table__table">
              <thead>
                <tr>
                  <th className="koinworks-table__no">No</th>
                  <th className="koinworks-table__document">Dokumen</th>
                  <th className="koinworks-table__married">Siswa belum menikah</th>
                  <th className="koinworks-table__married">Siswa sudah menikah</th>
                </tr>
              </thead>
              <tbody>
                {koinworksTableData.map((datum: Requirement, idx) => (
                  <tr key={`cell-${idx}`}>
                    <td className="koinworks-table__no">{`${idx + 1}.`}</td>
                    <td className="koinworks-table__document">
                      <div className="koinworks-table__flex">
                        {datum.document}
                        {datum.notes && (
                          <>
                            <span
                              className="koinworks-table__note-sp"
                              onClick={() => showNotes(datum.notes || '')}
                              onKeyDown={() => showNotes(datum.notes || '')}
                            >
                              <SVGIcon name="information" size="sm" color={theme.neutral.white} />
                            </span>
                            <a data-tip={datum.notes} data-for={`tooltip-${idx}`} className="koinworks-table__note-pc">
                              <SVGIcon name="information" size="sm" color={theme.neutral.white} />
                            </a>
                            <Tooltip id={`tooltip-${idx}`} place="bottom" />
                          </>
                        )}
                      </div>
                    </td>
                    <td className="koinworks-table__married">
                      {datum.studNotMarried && <SVGIcon name="check-round" size="sm" color={theme.campus.aqua30} />}
                    </td>
                    <td className="koinworks-table__married">
                      {datum.studMarried && <SVGIcon name="check-round" size="sm" color={theme.campus.aqua30} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </SectionContainer>
      <Modal isShown={modalIsShow}>
        <MarkdownContent content={notes} className="modal__text" />
        <Button variant="primary" onClick={() => setModalShow(false)} className="modal__close-btn" isFullWidth>
          Mengerti
        </Button>
      </Modal>
    </div>
  )
}

export default KoinworksTable
